import React from 'react';
import { Icon } from 'semantic-ui-react';
import { format, parseISO } from 'date-fns';
import '../styles.scss';
import {
  DealSummaryInvestorData,
  ActivityTimestamps,
  InvestorActivityChecklist as InvestorActivityChecklistType,
} from '~/types/portfolio/PortfolioDealSummary';

function formatTimestamp(ts: ActivityTimestamps): string {
  const first = parseISO(ts.firstAt);
  const last = parseISO(ts.lastAt);
  if (first.getDate() === last.getDate()) {
    return `${format(last, 'MM/dd/yy')}`;
  }
  return `${format(last, 'MM/dd/yy')}`;
}

export const CHECKLIST_ITEMS: { key: keyof InvestorActivityChecklistType; label: string }[] = [
  { key: 'viewedDeal', label: 'Viewed project / portfolio' },
  { key: 'downloadedSummary', label: 'Downloaded summary' },
  { key: 'downloadedSpreadsheet', label: 'Downloaded deal spreadsheet' },
  { key: 'downloadedAnyDataroomDocs', label: 'Accessed portfolio data room' },
  { key: 'accessedProjectDatarooms', label: 'Accessed project data rooms' },
];

export type ActivityChecklistContext = 'project' | 'portfolio';

function getViewedDealLabel(context: ActivityChecklistContext): string {
  return context === 'project' ? 'Viewed project' : 'Viewed portfolio';
}

function getDataroomLabel(context: ActivityChecklistContext, key: string): string | null {
  if (key === 'downloadedAnyDataroomDocs') {
    return context === 'project' ? 'Accessed project data room' : 'Accessed portfolio data room';
  }
  if (key === 'accessedProjectDatarooms') {
    return 'Accessed project data rooms';
  }
  return null;
}

/** For developer view (portfolio context): combine both dataroom items into one. Returns the more recent timestamp. */
function getCombinedDataroomValue(
  activity: InvestorActivityChecklistType | null | undefined
): ActivityTimestamps | null {
  const portfolio = activity?.downloadedAnyDataroomDocs ?? null;
  const project = activity?.accessedProjectDatarooms ?? null;
  if (!portfolio && !project) return null;
  if (!portfolio) return project;
  if (!project) return portfolio;
  return parseISO(portfolio.lastAt) >= parseISO(project.lastAt) ? portfolio : project;
}

/** Renders the activity checklist for a single investor. Use in PortfolioBidRow, PortfolioBidCards, ProjectBidRow, ProjectBidCards. */
export function InvestorActivityChecklistSingle({
  activity,
  context = 'portfolio',
  showDates = true,
  hasSummaryDoc,
}: {
  activity: InvestorActivityChecklistType | null | undefined;
  context?: ActivityChecklistContext;
  /** When false, only checkmarks are shown (no timestamps), and the deal spreadsheet row is hidden (admins only). Used for installers/developers. */
  showDates?: boolean;
  /** When false, the "Downloaded summary" item is hidden. Omit when unknown. */
  hasSummaryDoc?: boolean;
}) {
  const isDeveloperView = !showDates;
  const useCombinedDataroom = context === 'portfolio' && isDeveloperView;

  const baseItems = CHECKLIST_ITEMS.filter((item) => {
    if (context === 'project' && item.key === 'accessedProjectDatarooms') return false;
    if (item.key === 'downloadedSpreadsheet' && !showDates) return false;
    if (useCombinedDataroom && (item.key === 'downloadedAnyDataroomDocs' || item.key === 'accessedProjectDatarooms')) {
      return false;
    }
    if (item.key === 'downloadedSummary' && hasSummaryDoc !== true) return false;
    return true;
  });

  type ChecklistItemKey = keyof InvestorActivityChecklistType | 'combinedDataroom';
  const combinedItem: { key: ChecklistItemKey; label: string } = {
    key: 'combinedDataroom',
    label: 'Accessed data room',
  };
  const itemsToRender: { key: ChecklistItemKey; label: string }[] = useCombinedDataroom
    ? [...baseItems, combinedItem]
    : baseItems;

  return (
    <div className="portfolio-activity-checklist-list">
      {itemsToRender.map(({ key, label }) => {
        const isCombinedDataroom = key === 'combinedDataroom';
        const displayLabel =
          key === 'viewedDeal'
            ? getViewedDealLabel(context)
            : getDataroomLabel(context, key) ?? label;
        const value = isCombinedDataroom
          ? getCombinedDataroomValue(activity)
          : (activity?.[key as keyof InvestorActivityChecklistType] ?? null);
        const done = value != null;
        return (
          <div
            key={key}
            className={`portfolio-activity-checklist-item ${done ? 'complete' : 'incomplete'}`}
          >
            <div className="portfolio-activity-checklist-item-content">
              {done ? (
                <Icon name="check circle" color="green" />
              ) : (
                <Icon name="circle outline" />
              )}
              <span className="portfolio-activity-checklist-item-label">
                {displayLabel}
                {showDates && done && value && (
                  <div className="portfolio-activity-checklist-item-time">
                    ({formatTimestamp(value)})
                  </div>
                )}
              </span>
            </div>

          </div>
        );
      })}
    </div>
  );
}

/** Grid of activity checklists (one column per investor). Deprecated: prefer PortfolioBidTable / PortfolioBidCards. */
export default function InvestorActivityChecklist({
  investorData,
  context = 'portfolio',
}: {
  investorData: DealSummaryInvestorData[];
  context?: ActivityChecklistContext;
}) {
  return (
    <div
      className="portfolio-activity-checklist-container"
      style={{ ['--column-number' as string]: investorData?.length ?? 0 }}
    >
      <div
        className="grid-placeholder portfolio-activity-checklist-label"
        style={{ borderBottom: '1px solid var(--color-medium-grey)' }}
      >
        <span className="activity-checklist-title">Activity</span>
      </div>
      {investorData.map((investor) => (
        <div
          key={investor.id}
          className="portfolio-activity-checklist-cell"
          style={{ borderBottom: '1px solid var(--color-medium-grey)' }}
        >
          <InvestorActivityChecklistSingle activity={investor.activity} context={context} />
        </div>
      ))}
    </div>
  );
}
